import { clsx } from 'clsx'
import { type FC, type ReactElement } from 'react'
import { FormattedMessage, type IntlShape, useIntl } from 'react-intl'

import { PrescriptionPrescriptionTypeEnum } from '@redteclab/api/clients/bully'

import {
  type PublicGlobalConfig,
  useGlobalConfigContext,
} from '../../../../../global-config'
import { priceGetFormattedPrice } from '../../../../../price'
import { TosSuperscript } from '../../../../../tos-superscripts'
import { priceAccessorIsPrescription } from '../../../../model/priceAccessor/priceAccessorIsPrescription'
import { type ProductPrices } from '../../../ProductBuyBox/hooks/useProductPrices'

type ProductAttributeTileStrikeThroughPriceProps = {
  currentPrescriptionType: PrescriptionPrescriptionTypeEnum
  isLarge: boolean
  prices: ProductPrices
}

const getStrikeThroughPriceClass = ({
  isLarge,
  isPrescription,
}: {
  isLarge: boolean
  isPrescription: boolean
}): string =>
  clsx(
    'text-s',
    isLarge && 'flex justify-end',
    isPrescription && 'font-medium text-dark-primary-max',
    !isPrescription && 'text-dark-primary-strong',
  )

const getOriginalPrice = ({
  intl,
  prices,
  superScripts,
}: {
  intl: IntlShape
  prices: ProductPrices
  superScripts: PublicGlobalConfig['superScripts']
}): ReactElement | null => {
  const {
    displayPrice,
    isThirtyDayPriceTrendDown,
    showThirtyDaysPrice,
    strikeThroughOfferPrice,
    thirtyDaysLowestPrice,
  } = prices

  if (showThirtyDaysPrice && isThirtyDayPriceTrendDown) {
    return (
      <span className="flex flex-col gap-x-1 text-xs">
        <span>
          <FormattedMessage id="general.label.thirtyDaysPrice" />
          <TosSuperscript value={superScripts.thirtyDaysPrice} />
        </span>
        <span>
          {priceGetFormattedPrice(thirtyDaysLowestPrice?.amount ?? 0, intl)}
        </span>
      </span>
    )
  }

  const displayPriceValue = displayPrice?.price ?? 0
  const strikeThroughOfferPriceValue = strikeThroughOfferPrice?.price ?? 0

  if (displayPriceValue < strikeThroughOfferPriceValue) {
    return (
      <span className="flex flex-col gap-x-1 text-xs">
        <span className="flex h-[18px] w-full" />
        <span
          className={clsx(
            strikeThroughOfferPriceValue !== displayPrice && 'line-through',
          )}
        >
          {priceGetFormattedPrice(strikeThroughOfferPrice?.price ?? 0, intl)}
        </span>
      </span>
    )
  }

  return (
    <span className="flex flex-col gap-x-1 text-xs">
      <span className="flex h-[18px] w-full" />
      <span className="flex h-[18px] w-full" />
    </span>
  )
}

export const ProductAttributeTileStrikeThroughPrice: FC<
  ProductAttributeTileStrikeThroughPriceProps
> = ({ currentPrescriptionType, isLarge, prices }) => {
  const intl = useIntl()
  const { superScripts } = useGlobalConfigContext()

  const isPrescription = priceAccessorIsPrescription(currentPrescriptionType)
  const isPrescriptionButNotPrivate =
    isPrescription &&
    currentPrescriptionType !==
      PrescriptionPrescriptionTypeEnum.PrivatePrescription

  return (
    <li
      className={getStrikeThroughPriceClass({
        isLarge,
        isPrescription,
      })}
    >
      {isPrescriptionButNotPrivate ? (
        <span className="text-xs">
          <FormattedMessage id="general.label.additionalCharge" />
        </span>
      ) : null}
      {isPrescription
        ? null
        : getOriginalPrice({
            intl,
            prices,
            superScripts,
          })}
    </li>
  )
}
